/**
 * Tiny fetch wrapper. Uses cookies for auth (Knox token in HTTP-only cookie set
 * by Django on /auth/login). Reads CSRF token from the `csrftoken` cookie and
 * mirrors it into the X-CSRFToken header on unsafe methods.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match && match[2] ? decodeURIComponent(match[2]) : null;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T = unknown>(
  path: string,
  opts: {
    method?: Method;
    body?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = getCookie('csrftoken');
    if (csrf) headers['X-CSRFToken'] = csrf;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text();
    let payload: unknown;
    try { payload = JSON.parse(text); } catch { payload = text; }
    throw Object.assign(new Error(`API ${res.status}`), { status: res.status, payload });
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
